import { Spinner } from "@pej/kore/components";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { clearReauthSentinel } from "@/api/reauth";
import { workspacesQueryOptions } from "@/api/workspaces";
import { AppShell } from "@/components/layout/app-shell";
import { readStoredId, useWorkspaceContext, WorkspaceProvider } from "@/contexts/workspace-context";

// `wsRedirected` is set by the invalid-:workspaceId guard below when it
// bounces a stale/unknown URL to the user's default workspace — carries the
// id that didn't resolve, for a future toast surface to consume.
//
// `reauth` is the loop-guard sentinel set by redirectToSso (src/api/reauth.ts).
// Accepted here so TanStack Router doesn't reject the SSO round-trip URL;
// clearReauthSentinel strips it below once the workspaces query resolves.
//
// It is `coerce`, not a bare string, because the router hands us a NUMBER.
// TanStack's defaultParseSearch is parseSearchWith(JSON.parse) and it runs the
// parser over every search value, swallowing failures — so `?reauth=1` arrives
// as `1`, and a bare z.string() rejects it. That rejection throws before
// beforeLoad runs, so clearReauthSentinel never gets to strip the param and
// the whole route is dead for the rest of the session: the app fails to load
// with a Zod error every time an SSO round-trip lands back here. Any future
// numeric-looking search param needs the same treatment.
export const searchSchema = z.object({
    // Not coerced: workspace ids are non-numeric, so JSON.parse throws on them
    // and the raw string survives.
    wsRedirected: z.string().optional(),
    reauth: z.coerce.string().optional(),
});

export const Route = createFileRoute("/w/$workspaceId")({
    validateSearch: searchSchema,
    beforeLoad: async ({ context, params }) => {
        const workspaces = await context.queryClient.ensureQueryData(workspacesQueryOptions);
        clearReauthSentinel();

        if (workspaces.some((w) => w.id === params.workspaceId)) {
            return;
        }

        // URL carries an id the user can't (or no longer can) access. With
        // zero accessible workspaces there's nothing to fall back to.
        if (workspaces.length === 0) {
            throw redirect({ to: "/" });
        }

        const storedId = readStoredId();
        const stored = storedId ? workspaces.find((w) => w.id === storedId) : undefined;
        // biome-ignore lint/style/noNonNullAssertion: workspaces.length > 0 guarded above
        const fallback = stored ?? workspaces[0]!;
        throw redirect({
            to: "/w/$workspaceId",
            params: { workspaceId: fallback.id },
            search: { wsRedirected: params.workspaceId },
        });
    },
    component: WorkspaceLayout,
});

function WorkspaceLayout() {
    return (
        <WorkspaceProvider>
            <WorkspaceGate />
        </WorkspaceProvider>
    );
}

// Mounted inside WorkspaceProvider so the shell's useWorkspaceContext calls
// resolve. beforeLoad already ensured the workspaces query above, so this
// only shows while that promise's result is still propagating to the
// component tree (effectively instant outside of tests that render the
// provider directly against an unresolved query).
function WorkspaceGate() {
    const { isLoading } = useWorkspaceContext();

    if (isLoading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <Spinner />
            </div>
        );
    }

    return (
        <AppShell>
            <Outlet />
        </AppShell>
    );
}
